import React from 'react';
import type { SiteEntity } from '../../types/site';
import { materializeSiteBoundaries, aggregateBbox, isFiniteLatLng } from '../../lib/siteBoundaryGeometry';
import { cn } from '@/lib/utils';
// Shared Aurora per-parcel palette (single source of truth) so the thumbnail
// draws the same hues, in the same order, as the at-a-glance card + live map.
import { AURORA_PARCEL_COLORS as PARCEL_COLORS } from '../../config/siteLayerColors';

function boundaryToSvgPoints(
  boundaryInput: { lat: number; lng: number }[],
  w: number,
  h: number,
  pad: number,
  bbox?: { south: number; west: number; north: number; east: number },
): string | null {
  // HOTFIX(lat-crash): per-vertex filter (NOT just length<3). The sibling
  // HeroCoverBoundaryOverlay helper was hardened in the first hotfix but this
  // one was missed — a parcel with a nullish / non-finite vertex reaching this
  // site-card / landing render path threw "reading 'lat'" in the .map below.
  const boundary = Array.isArray(boundaryInput) ? boundaryInput.filter(isFiniteLatLng) : [];
  if (boundary.length < 3) return null;
  const lats = boundary.map(p => p.lat);
  const lngs = boundary.map(p => p.lng);
  // Math.min(...arr) passes every element as an argument and throws
  // "Maximum call stack size exceeded" on a large boundary — this renders on
  // the site card while map layers are being merged, so a dense parcel set
  // took the whole page down through the ErrorBoundary (live 2026-08-05).
  const extent = (values: readonly number[]): { min: number; max: number } => {
    let min = Infinity;
    let max = -Infinity;
    for (const v of values) { if (v < min) min = v; if (v > max) max = v; }
    return { min, max };
  };
  const latExtent = extent(lats);
  const lngExtent = extent(lngs);
  const minLat = bbox?.south ?? latExtent.min;
  const maxLat = bbox?.north ?? latExtent.max;
  const minLng = bbox?.west ?? lngExtent.min;
  const maxLng = bbox?.east ?? lngExtent.max;
  const spanLat = (maxLat - minLat) || 1e-6;
  const spanLng = (maxLng - minLng) || 1e-6;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  const fit = Math.min(innerW / spanLng, innerH / spanLat);
  const usedW = spanLng * fit;
  const usedH = spanLat * fit;
  const xOffset = pad + (innerW - usedW) / 2;
  const yOffset = pad + (innerH - usedH) / 2;
  return boundary
    .map(p => {
      const x = xOffset + (p.lng - minLng) * fit;
      const y = yOffset + (maxLat - p.lat) * fit;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

const VIEW_W = 260;
const VIEW_H = 130;
const PAD = 12;

export function siteTotalAcres(site: SiteEntity): number | null {
  const fromAggregate = site.siteBoundaryAggregate?.totalArea;
  if (fromAggregate != null && Number.isFinite(fromAggregate)) return fromAggregate;
  if (site.siteArea != null && Number.isFinite(site.siteArea)) return site.siteArea;
  return null;
}

export function formatSiteAcres(acres: number): string {
  const rounded = Math.round(acres);
  return `${rounded.toLocaleString('en-US')} ac`;
}

/** viewBox aspect ratio — width:height = 260:130 = 2:1 */
export const BOUNDARY_THUMB_ASPECT = VIEW_W / VIEW_H;

/** Size sidebar float thumb to card height (minus gap), capped to ~50% card width. */
export function sidebarThumbDimensions(
  cardWidth: number,
  cardHeight: number,
  gapPx = 6,
  maxWidthRatio = 0.5,
): { width: number; height: number } {
  const targetH = Math.max(52, cardHeight - gapPx);
  let height = targetH;
  let width = height * BOUNDARY_THUMB_ASPECT;
  const maxW = cardWidth * maxWidthRatio;
  if (width > maxW) {
    width = maxW;
    height = width / BOUNDARY_THUMB_ASPECT;
  }
  return { width, height };
}

export function SiteBoundaryThumbnail({
  site,
  className,
  layout = 'default',
  testId,
}: {
  site: SiteEntity;
  className?: string;
  /**
   * `sidebar` — float-right card rail: transparent bg, taller thumb.
   * `hero` — full-width banner strip (Atlas card top): the parcel fills
   *   the strip, transparent bg, no border (the caller frames it). The
   *   wrapper stretches to its container so a parent-set height drives the
   *   strip; the SVG fits the polygon inside via `preserveAspectRatio`.
   */
  layout?: 'default' | 'sidebar' | 'hero';
  testId?: string;
}) {
  const parcels = React.useMemo(
    () =>
      materializeSiteBoundaries({
        siteBoundaries: site.siteBoundaries,
        siteBoundary: site.siteBoundary,
        siteArea: site.siteArea,
        sitePerimeter: site.sitePerimeter,
        siteCentroid: site.siteCentroid,
      }),
    [site.siteBoundaries, site.siteBoundary, site.siteArea, site.sitePerimeter, site.siteCentroid],
  );

  // Fast bounding-box projection only — never run computeAggregate
  // (polygonClipping.union + pairwise Haversine disjoint scan) synchronously on
  // the main thread across 32 sidebar/dashboard thumbnail cards (#240).
  const bbox = React.useMemo(() => {
    if (parcels.length === 0) return null;
    if (site.siteBoundaryAggregate?.bbox) return site.siteBoundaryAggregate.bbox;
    return aggregateBbox(parcels);
  }, [parcels, site.siteBoundaryAggregate]);

  const multiParcel = parcels.length > 1;

  const isSidebar = layout === 'sidebar';
  const isHero = layout === 'hero';
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const [sidebarDims, setSidebarDims] = React.useState<{ width: number; height: number } | null>(null);

  React.useEffect(() => {
    if (!isSidebar) return;
    const el = wrapRef.current;
    if (!el) return;
    const card = el.closest('[data-site-card]') as HTMLElement | null;
    if (!card) return;
    const textCol = card.querySelector('[data-site-card-text]') as HTMLElement | null;

    const measure = () => {
      const cardRect = card.getBoundingClientRect();
      const textH = textCol?.getBoundingClientRect().height ?? cardRect.height;
      setSidebarDims(sidebarThumbDimensions(cardRect.width, textH));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(card);
    if (textCol) ro.observe(textCol);
    return () => ro.disconnect();
  }, [isSidebar, site.id]);

  const sidebarStyle = isSidebar && sidebarDims
    ? { width: sidebarDims.width, height: sidebarDims.height }
    : undefined;

  const sidebarClass = isSidebar
    ? 'float-right ml-2 mb-0.5 shrink-0'
    : '';

  if (!bbox || parcels.length === 0) {
    // No drawable geometry → COLLAPSE the frame entirely rather than
    // reserving a large empty dashed/grey placeholder box. The sidebar
    // layout floats right, so collapsing recovers the full card width for
    // the title/meta column; the default layout simply renders nothing.
    // A zero-size, test-targetable marker is kept so callers (and the
    // existing tests) can still assert "no thumbnail was drawn" without a
    // visible empty box.
    return (
      <span
        ref={wrapRef as unknown as React.RefObject<HTMLSpanElement>}
        className="hidden"
        data-testid={testId ?? `site-boundary-thumb-${site.id}`}
        data-thumb-empty="true"
        aria-hidden
      />
    );
  }

  return (
    <div
      ref={wrapRef}
      style={isSidebar ? sidebarStyle : undefined}
      className={cn(
        isHero
          // Hero banner: fill the caller-framed strip, transparent so the
          // verdict-tint overlay shows through; no own border/radius (the
          // card rounds the strip's top corners).
          ? 'bg-transparent overflow-hidden w-full h-full'
          : 'bg-transparent overflow-visible shrink-0',
        !isHero && isSidebar
          ? cn(sidebarClass, !sidebarDims && 'w-[8.25rem] aspect-[260/130]')
          : '',
        !isHero && !isSidebar
          ? 'border border-border-subtle bg-state-hover overflow-hidden rounded-lg'
          : '',
        className,
      )}
      data-testid={testId ?? `site-boundary-thumb-${site.id}`}
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className={cn('block w-full h-full', isSidebar || isHero ? '' : 'h-auto')}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={multiParcel ? `Site boundary — ${parcels.length} parcels` : 'Site boundary polygon'}
      >
        {parcels.map((p, i) => {
          const points = boundaryToSvgPoints(p.polygon, VIEW_W, VIEW_H, PAD, bbox);
          if (!points) return null;
          const c = PARCEL_COLORS[i % PARCEL_COLORS.length];
          return (
            <polygon
              key={p.id}
              points={points}
              fill={multiParcel ? c.fill : undefined}
              stroke={multiParcel ? c.stroke : undefined}
              className={multiParcel ? '' : 'fill-primary/15 stroke-primary'}
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
          );
        })}
      </svg>
    </div>
  );
}
